/*  ===================================
 *  CART EMAIL CAPTURE
 *  - "Save your cart" form in the cart drawer. Markup: snippets/cart-email-capture.liquid
 *  - Submit writes the Cart Capture Source and Cart Recovery URL profile properties
 *    and a "Saved Cart" event (the Klaviyo flow's trigger; flows can't trigger on a
 *    profile property) straight to Klaviyo's client API. Not via _learnq: klaviyo.js
 *    is often blocked (private windows, ad blockers) and then nothing reached Klaviyo.
 *    _learnq identify still runs, to cookie the visitor when klaviyo.js is present.
 *  - Opt-in ticked: also subscribes them to the marketing list in the data-list-id.
 *  - Collapsed to one line until tapped. Shown to everyone, but not again this
 *    session once submitted. Logged-in customers (data-customer-email) skip the
 *    email field; with no opt-in to ask (data-cec-instant) the tap itself saves.
 *  ===================================
 */

(function (w, d) {
  const container = d.getElementById('cart-email-capture');
  if (!container) return;

  const SOURCE = 'Cart Drawer';
  const CAPTURED_KEY = 'hueseeka_cart_email_captured';
  const EMAIL_KEY = 'hueseeka_cart_email';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

  const toggle = container.querySelector('[data-cec-toggle]');
  const close = container.querySelector('[data-cec-close]');
  const form = container.querySelector('[data-cec-form]');
  const input = container.querySelector('[data-cec-input]');
  const button = container.querySelector('[data-cec-submit]');
  const optin = container.querySelector('[data-cec-optin]');
  const error = container.querySelector('[data-cec-error]');
  const success = container.querySelector('[data-cec-success]');

  const customerEmail = container.dataset.customerEmail || '';

  let submitted = false;
  let saving = false;
  let lastRecoveryUrl = null;

  function storage(method, key, value) {
    try {
      return w.sessionStorage[method](key, value);
    } catch (e) {
      return null;
    }
  }

  function learnq() {
    w._learnq = w._learnq || [];
    return w._learnq;
  }

  // A cookie written by klaviyo.js. Anonymous visitors carry only a "cid"; it can
  // gain $email once they're identified (form, email click, etc).
  function cookieEmail() {
    const match = d.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
    if (!match) return '';
    try {
      const data = JSON.parse(atob(decodeURIComponent(match[1])));
      return typeof data.$email === 'string' ? data.$email : '';
    } catch (e) {
      return '';
    }
  }

  function setOpen(open) {
    toggle.hidden = open;
    toggle.setAttribute('aria-expanded', String(open));
    form.hidden = !open;
    if (open && input && !input.value) input.value = cookieEmail();
    if (!open) toggle.focus({ preventScroll: true });
  }

  function fetchCart() {
    return fetch('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(
      function (res) {
        return res.json();
      }
    );
  }

  // Shopify cart permalink: /cart/VARIANT:QTY,VARIANT:QTY. Line item properties
  // don't survive it, so lines of the same variant are merged.
  function recoveryUrl(cart) {
    const qty = {};
    (cart.items || []).forEach(function (item) {
      qty[item.variant_id] = (qty[item.variant_id] || 0) + item.quantity;
    });
    const pairs = Object.keys(qty).map(function (id) {
      return id + ':' + qty[id];
    });
    if (!pairs.length) return null;
    const base = (container.dataset.shopUrl || w.location.origin).replace(/\/$/, '');
    return base + '/cart/' + pairs.join(',');
  }

  function savedCartEvent(cart, url) {
    return {
      $value: cart.total_price / 100,
      CartRecoveryURL: url,
      ItemCount: cart.item_count,
      ItemNames: cart.items.map(function (item) {
        return item.product_title;
      }),
      Items: cart.items.map(function (item) {
        return {
          ProductName: item.product_title,
          VariantName: item.variant_title,
          ProductID: item.product_id,
          VariantID: item.variant_id,
          SKU: item.sku,
          Quantity: item.quantity,
          Price: item.final_price / 100,
          LinePrice: item.final_line_price / 100,
          URL: w.location.origin + item.url,
          ImageURL: item.image,
        };
      }),
    };
  }

  function klaviyoClient(endpoint, data) {
    const url = 'https://a.klaviyo.com/client/' + endpoint + '/?company_id=' + container.dataset.companyId;
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
      body: JSON.stringify({ data: data }),
    }).then(function (res) {
      if (!res.ok) throw new Error('Klaviyo ' + endpoint + ' failed: ' + res.status);
    });
  }

  function profileData(email, properties) {
    return { data: { type: 'profile', attributes: { email: email, properties: properties } } };
  }

  function updateProfile(email, properties) {
    return klaviyoClient('profiles', profileData(email, properties).data);
  }

  // One request: upserts the profile with its properties and records the event.
  function trackSavedCart(email, properties, cart, url) {
    return klaviyoClient('events', {
      type: 'event',
      attributes: {
        metric: { data: { type: 'metric', attributes: { name: 'Saved Cart' } } },
        profile: profileData(email, properties),
        properties: savedCartEvent(cart, url),
        value: cart.total_price / 100,
      },
    });
  }

  function subscribe(email) {
    const listId = container.dataset.listId;
    if (!listId) return Promise.resolve();
    return klaviyoClient('subscriptions', {
      type: 'subscription',
      attributes: {
        custom_source: SOURCE,
        profile: { data: { type: 'profile', attributes: { email: email } } },
      },
      relationships: { list: { data: { type: 'list', id: listId } } },
    });
  }

  function showError(message) {
    form.hidden = false;
    toggle.hidden = true;
    error.textContent = message;
    if (!input) return;
    input.setAttribute('aria-invalid', 'true');
    input.focus();
  }

  function onSubmit() {
    if (submitted || saving) return;
    const email = customerEmail || input.value.trim();
    if (!EMAIL_RE.test(email)) {
      showError('Please enter a valid email address.');
      return;
    }
    error.textContent = '';
    if (input) input.removeAttribute('aria-invalid');
    saving = true;
    button.disabled = true;
    toggle.setAttribute('aria-busy', 'true');

    const subscribeRequested = Boolean(optin && optin.checked);

    fetchCart()
      .catch(function () {
        return { items: [], item_count: 0, total_price: 0 };
      })
      .then(function (cart) {
        const url = recoveryUrl(cart);
        const properties = { 'Cart Capture Source': SOURCE };
        if (url) properties['Cart Recovery URL'] = url;

        // Cookies the visitor so klaviyo.js tracks their cart from here on.
        learnq().push(['identify', Object.assign({ $email: email }, properties)]);

        const saved = url ? trackSavedCart(email, properties, cart, url) : updateProfile(email, properties);
        // The opt-in is best effort: the save is what matters.
        const subscribed = subscribeRequested ? subscribe(email).catch(console.warn) : null;

        return Promise.all([saved, subscribed]).then(function () {
          lastRecoveryUrl = url;
          submitted = true;
          storage('setItem', CAPTURED_KEY, 'true');
          storage('setItem', EMAIL_KEY, email);
          toggle.hidden = true;
          form.hidden = true;
          success.hidden = false;
        });
      })
      .catch(function (e) {
        console.warn(e);
        showError('Sorry, we couldn\'t save your cart. Please try again.');
      })
      .then(function () {
        saving = false;
        button.disabled = false;
        toggle.removeAttribute('aria-busy');
      });
  }

  // Keep the profile's Cart Recovery URL current as the cart changes after a save.
  function onCartUpdated() {
    const email = storage('getItem', EMAIL_KEY);
    if (!email) return;
    fetchCart().then(function (cart) {
      const url = recoveryUrl(cart);
      if (!url || url === lastRecoveryUrl) return;
      lastRecoveryUrl = url;
      updateProfile(email, { 'Cart Recovery URL': url }).catch(console.warn);
    });
  }

  toggle.addEventListener('click', function () {
    if (toggle.hasAttribute('data-cec-instant')) return onSubmit();
    setOpen(true);
  });

  close.addEventListener('click', function () {
    setOpen(false);
  });

  button.addEventListener('click', onSubmit);

  if (input) {
    // Enter in the email field would otherwise submit #CartDrawerForm, i.e. checkout.
    input.addEventListener('keydown', function (evt) {
      if (evt.key !== 'Enter') return;
      evt.preventDefault();
      onSubmit();
    });

    input.addEventListener('input', function () {
      if (!error.textContent) return;
      error.textContent = '';
      input.removeAttribute('aria-invalid');
    });
  }

  d.addEventListener('cart:updated', onCartUpdated);

  container.hidden = Boolean(storage('getItem', CAPTURED_KEY));
})(window, document);
